import { useState, useEffect, useCallback, useMemo } from 'react';
import { PersonaId } from '../config/personas';

/**
 * usePersonaManager - React hook for persona system integration
 *
 * Provides reactive access to PersonaManager state:
 * - Active persona
 * - Available personas
 * - Current mode
 * - Persona switching
 *
 * View state (2D/3D mode, planeId) is managed by the useViewManager hook,
 * which subscribes directly to potree's ViewManager via useSyncExternalStore.
 */

/**
 * Subset of IPersona interface from potree_fork
 * Contains only the properties needed for UI state management
 */
interface PersonaInfo {
  name: string;
  enabled: boolean;
  priority: number;
}

/**
 * Full persona object with methods from potree_fork IPersona
 * Used when we need to interact with persona methods
 */
type PersonaEventHandler = (event?: Record<string, unknown>) => void;

interface PersonaObject extends PersonaInfo {
  addEventListener(event: string, handler: PersonaEventHandler): void;
  removeEventListener(event: string, handler: PersonaEventHandler): void;
  setMode?(modeId: string): void;
  getCurrentMode?(): ModeInfo | undefined;
  getAvailableModes?(): ModeInfo[];
}

/**
 * Subset of IPersonaMode interface from potree_fork
 * Contains only the properties needed for UI display
 */
interface ModeInfo {
  id: string;
  name: string;
  icon?: React.ComponentType | string;
}

/**
 * Viewer type with PersonaManager attached
 * The viewer object from Potree with the persona system extensions
 */
interface ViewerWithPersonaManager {
  personaManager?: {
    getAllPersonas(): PersonaObject[];
    getActivePersona(): PersonaObject | undefined;
    enablePersona(name: string): void;
    disablePersona(name: string): void;
    togglePersona(name: string): void;
    addEventListener(event: string, handler: () => void): void;
    removeEventListener(event: string, handler: () => void): void;
  };
}

interface UsePersonaManagerResult {
  /** Currently active persona (top of stack) */
  activePersona?: PersonaInfo;
  /** All registered personas */
  personas: PersonaInfo[];
  /** Current mode of active persona */
  currentMode?: ModeInfo;
  /** Available modes for active persona */
  availableModes: ModeInfo[];
  /** Disabled modes (e.g., when requirements not met) */
  disabledModes: Set<string>;
  /** Enable a persona */
  enablePersona: (name: PersonaId) => void;
  /** Disable a persona */
  disablePersona: (name: PersonaId) => void;
  /** Toggle a persona */
  togglePersona: (name: PersonaId) => void;
  /** Switch to a persona (radio button behavior) */
  switchPersona: (name: PersonaId) => void;
  /** Switch persona mode */
  setMode: (modeId: string) => void;
  /** Whether persona system is initialized */
  isReady: boolean;
}

/**
 * Hook to interact with PersonaManager
 *
 * @param viewer - Potree Viewer instance
 * @returns Persona manager state and actions
 *
 * @example
 * const { activePersona, enablePersona } = usePersonaManager(viewer);
 *
 * // Enable a persona
 * <button onClick={() => enablePersona('measure')}>Measure</button>
 *
 * // Show active persona
 * <div>{activePersona?.name}</div>
 */
export function usePersonaManager(viewer: ViewerWithPersonaManager | undefined): UsePersonaManagerResult {
  'use no memo';
  const isReady = useMemo(() => !!viewer?.personaManager, [viewer]);
  const [activePersona, setActivePersona] = useState<PersonaInfo | undefined>();
  const [personas, setPersonas] = useState<PersonaInfo[]>([]);
  const [currentMode, setCurrentMode] = useState<ModeInfo | undefined>();
  const [availableModes, setAvailableModes] = useState<ModeInfo[]>([]);
  const [disabledModes, setDisabledModes] = useState<Set<string>>(new Set());

  // Update state from PersonaManager
  const updateState = useCallback(() => {
    if (!viewer?.personaManager) return;

    const manager = viewer.personaManager;

    // Get all personas
    const allPersonas = manager.getAllPersonas().map(
      (p: PersonaObject): PersonaInfo => ({
        name: p.name,
        enabled: p.enabled,
        priority: p.priority,
      }),
    );
    setPersonas(allPersonas);

    // Get active persona (top of stack)
    const active = manager.getActivePersona();
    if (active) {
      setActivePersona({
        name: active.name,
        enabled: active.enabled,
        priority: active.priority,
      });

      // Get current mode (if supported)
      if (active.getCurrentMode) {
        const mode = active.getCurrentMode();
        if (mode) {
          setCurrentMode({
            id: mode.id,
            name: mode.name,
            icon: mode.icon,
          });
        } else {
          setCurrentMode(undefined);
        }
      } else {
        setCurrentMode(undefined);
      }

      // Get available modes (if supported)
      if (active.getAvailableModes) {
        const modes = active.getAvailableModes().map(
          (m: ModeInfo): ModeInfo => ({
            id: m.id,
            name: m.name,
            icon: m.icon,
          }),
        );
        setAvailableModes(modes);
      } else {
        setAvailableModes([]);
      }
    } else {
      setActivePersona(undefined);
      setCurrentMode(undefined);
      setAvailableModes([]);
    }
  }, [viewer]);

  // Initialize and subscribe to events
  useEffect(() => {
    if (!viewer?.personaManager) {
      return;
    }

    // Defer initial state sync to avoid synchronous setState in effect body
    queueMicrotask(updateState);

    // Subscribe to persona manager events
    const handleStackChanged = () => {
      updateState();
    };

    viewer.personaManager.addEventListener('persona_manager_stack_changed', handleStackChanged);

    // Listen to individual persona events for mode changes and tool availability
    const allPersonas = viewer.personaManager.getAllPersonas();
    const eventHandlers: Array<{ persona: PersonaObject; event: string; handler: PersonaEventHandler }> = [];

    allPersonas.forEach((persona: PersonaObject) => {
      const modeHandler = () => updateState();
      const toolAvailabilityHandler = (event?: Record<string, unknown>) => {
        // Disable the room/wall/opening/structure creation modes when no floors
        // exist. These all operate on a floor, so they require at least one floor to
        // be created first (the 'floor_select' mode stays enabled to create one).
        // The 'create floor first' tooltip for each is configured in config/personas.tsx.
        if (event?.floorsExist === false) {
          setDisabledModes(new Set(['floorplan', 'wall', 'design', 'structure']));
        } else if (event?.floorsExist === true) {
          setDisabledModes(new Set());
        }
      };

      persona.addEventListener('mode_changed', modeHandler);
      persona.addEventListener('tool_availability_changed', toolAvailabilityHandler);

      eventHandlers.push({ persona, event: 'mode_changed', handler: modeHandler }, { persona, event: 'tool_availability_changed', handler: toolAvailabilityHandler });
    });

    return () => {
      if (viewer?.personaManager) {
        viewer.personaManager.removeEventListener('persona_manager_stack_changed', handleStackChanged);
      }
      eventHandlers.forEach(({ persona, event, handler }) => {
        persona.removeEventListener(event, handler);
      });
    };
  }, [viewer, updateState]);

  // Actions
  const enablePersona = useCallback(
    (name: PersonaId) => {
      if (!viewer?.personaManager) return;
      try {
        viewer.personaManager.enablePersona(name);
      } catch (error) {
        console.error(`Failed to enable persona "${name}":`, error);
      }
    },
    [viewer],
  );

  const disablePersona = useCallback(
    (name: PersonaId) => {
      if (!viewer?.personaManager) return;
      try {
        viewer.personaManager.disablePersona(name);
      } catch (error) {
        console.error(`Failed to disable persona "${name}":`, error);
      }
    },
    [viewer],
  );

  const togglePersona = useCallback(
    (name: PersonaId) => {
      if (!viewer?.personaManager) return;
      try {
        viewer.personaManager.togglePersona(name);
      } catch (error) {
        console.error(`Failed to toggle persona "${name}":`, error);
      }
    },
    [viewer],
  );

  /**
   * Switch to a persona (radio button behavior)
   * - If persona is already active, do nothing
   * - If different persona is active, disable it and enable the new one
   * - Always ensures exactly one persona is active
   */
  const switchPersona = useCallback(
    (name: PersonaId) => {
      if (!viewer?.personaManager) return;

      const active = viewer.personaManager.getActivePersona();

      // If already active, do nothing
      if (active?.name === name) {
        return;
      }

      try {
        // Disable current persona if any
        if (active) {
          viewer.personaManager.disablePersona(active.name);
        }

        // Enable new persona
        viewer.personaManager.enablePersona(name);
      } catch (error) {
        console.error(`Failed to switch persona to "${name}":`, error);
      }
    },
    [viewer],
  );

  const setMode = useCallback(
    (modeId: string) => {
      if (!viewer?.personaManager) return;
      const active = viewer.personaManager.getActivePersona();
      if (!active) {
        console.warn('No active persona to set mode on');
        return;
      }
      if (!active.setMode) {
        console.warn('Active persona does not support modes');
        return;
      }
      try {
        active.setMode(modeId);
      } catch (error) {
        console.error(`Failed to set mode "${modeId}":`, error);
      }
    },
    [viewer],
  );

  return {
    activePersona,
    personas,
    currentMode,
    availableModes,
    disabledModes,
    enablePersona,
    disablePersona,
    togglePersona,
    switchPersona,
    setMode,
    isReady,
  };
}
